import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Save,
  Eye,
  ArrowLeft,
  Settings,
  Database,
  Layers,
  Filter,
  Palette,
  Download,
  Upload,
  Trash2,
  Play,
  Code,
  Table2,
  LineChart,
  Activity,
  DollarSign,
  Users,
  Target,
  Calendar,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Info
} from 'lucide-react';

// Import chart components for preview
import PieChartComponent from './charts/vis_piechart';
import BarChartComponent from './charts/vis_barchart';
import TrendChartComponent from './charts/vis_trendchart';

const VisStudio = () => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Studio State
  const [activeStep, setActiveStep] = useState(1);
  const [chartConfig, setChartConfig] = useState({
    id: '',
    name: '',
    description: '',
    type: 'bar', // pie, bar, trend
    enabled: true,
    dataSource: {
      entity: '', // customers, leads, opportunities, salesnotes, activities
      aggregation: 'count', // count, sum, avg, min, max
      groupBy: '',
      filters: []
    },
    dimensions: {
      xKey: '',
      yKey: '',
      dataKey: 'value',
      nameKey: 'name'
    },
    styling: {
      color: '#3b82f6',
      layout: 'vertical', // vertical, horizontal
      chartType: 'area', // area, line (for trend charts)
      showLegend: true,
      height: 400
    },
    formatting: {
      tooltipFormatter: '',
      labelFormat: '',
      valueFormat: 'number' // number, currency, percentage
    }
  });

  const [availableEntities, setAvailableEntities] = useState([]);
  const [entityFields, setEntityFields] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonEditorContent, setJsonEditorContent] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);

  // Data source entities configuration
  const dataSourceEntities = [
    {
      id: 'customers',
      name: 'Customers',
      icon: Users,
      description: 'Customer records and information',
      fields: [
        { name: 'CustomerID', type: 'number', label: 'Customer ID' },
        { name: 'CustomerName', type: 'string', label: 'Customer Name' },
        { name: 'Status', type: 'string', label: 'Status' },
        { name: 'Industry', type: 'string', label: 'Industry' },
        { name: 'CreatedAt', type: 'date', label: 'Created Date' },
        { name: 'UpdatedAt', type: 'date', label: 'Updated Date' },
        { name: 'TotalRevenue', type: 'number', label: 'Total Revenue' },
        { name: 'AssignedTo', type: 'string', label: 'Assigned To' }
      ]
    },
    {
      id: 'leads',
      name: 'Leads',
      icon: Target,
      description: 'Lead generation and tracking',
      fields: [
        { name: 'LeadID', type: 'number', label: 'Lead ID' },
        { name: 'LeadName', type: 'string', label: 'Lead Name' },
        { name: 'Status', type: 'string', label: 'Status' },
        { name: 'Source', type: 'string', label: 'Source' },
        { name: 'Score', type: 'number', label: 'Lead Score' },
        { name: 'CreatedAt', type: 'date', label: 'Created Date' },
        { name: 'ConvertedAt', type: 'date', label: 'Converted Date' },
        { name: 'AssignedTo', type: 'string', label: 'Assigned To' }
      ]
    },
    {
      id: 'opportunities',
      name: 'Opportunities',
      icon: TrendingUp,
      description: 'Sales opportunities and pipeline',
      fields: [
        { name: 'OpportunityID', type: 'number', label: 'Opportunity ID' },
        { name: 'OpportunityName', type: 'string', label: 'Opportunity Name' },
        { name: 'Stage', type: 'string', label: 'Stage' },
        { name: 'Status', type: 'string', label: 'Status' },
        { name: 'Amount', type: 'number', label: 'Amount' },
        { name: 'Probability', type: 'number', label: 'Probability (%)' },
        { name: 'CreatedAt', type: 'date', label: 'Created Date' },
        { name: 'ClosedAt', type: 'date', label: 'Closed Date' },
        { name: 'AssignedTo', type: 'string', label: 'Assigned To' }
      ]
    },
    {
      id: 'activities',
      name: 'Activities',
      icon: Activity,
      description: 'Customer activities and interactions',
      fields: [
        { name: 'ActivityID', type: 'number', label: 'Activity ID' },
        { name: 'ActivityType', type: 'string', label: 'Activity Type' },
        { name: 'Subject', type: 'string', label: 'Subject' },
        { name: 'Status', type: 'string', label: 'Status' },
        { name: 'Priority', type: 'string', label: 'Priority' },
        { name: 'CreatedAt', type: 'date', label: 'Created Date' },
        { name: 'DueDate', type: 'date', label: 'Due Date' },
        { name: 'CompletedAt', type: 'date', label: 'Completed Date' },
        { name: 'AssignedTo', type: 'string', label: 'Assigned To' }
      ]
    },
    {
      id: 'salesnotes',
      name: 'Notes',
      icon: Table2,
      description: 'Notes and comments',
      fields: [
        { name: 'NoteID', type: 'number', label: 'Note ID' },
        { name: 'Category', type: 'string', label: 'Category' },
        { name: 'Sentiment', type: 'string', label: 'Sentiment' },
        { name: 'CreatedAt', type: 'date', label: 'Created Date' },
        { name: 'CreatedBy', type: 'string', label: 'Created By' },
        { name: 'CustomerID', type: 'number', label: 'Customer ID' }
      ]
    }
  ];

  // Chart type definitions
  const chartTypes = [
    {
      id: 'bar',
      name: 'Bar Chart',
      icon: BarChart3,
      description: 'Compare values across categories',
      dimensions: ['xKey', 'yKey'],
      layouts: ['vertical', 'horizontal']
    },
    {
      id: 'pie',
      name: 'Pie Chart',
      icon: PieChart,
      description: 'Show proportions and percentages',
      dimensions: ['nameKey', 'dataKey'],
      layouts: []
    },
    {
      id: 'trend',
      name: 'Trend Chart',
      icon: TrendingUp,
      description: 'Display trends over time',
      dimensions: ['xKey', 'dataKey'],
      layouts: [],
      chartTypes: ['area', 'line']
    }
  ];

  // Aggregation functions
  const aggregationFunctions = [
    { id: 'count', name: 'Count', description: 'Count of records' },
    { id: 'sum', name: 'Sum', description: 'Sum of values' },
    { id: 'avg', name: 'Average', description: 'Average of values' },
    { id: 'min', name: 'Minimum', description: 'Minimum value' },
    { id: 'max', name: 'Maximum', description: 'Maximum value' }
  ];

  // Load saved templates
  useEffect(() => {
    loadSavedTemplates();
  }, []);

  // Update entity fields when entity changes
  useEffect(() => {
    if (chartConfig.dataSource.entity) {
      const entity = dataSourceEntities.find(e => e.id === chartConfig.dataSource.entity);
      if (entity) {
        setEntityFields(entity.fields);
      }
    }
  }, [chartConfig.dataSource.entity]);

  const loadSavedTemplates = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch('/api/visualization/templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSavedTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const fetchPreviewData = async () => {
    setLoading(true);
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch('/api/visualization/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify(chartConfig)
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching preview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveVisualizationTemplate = async () => {
    if (!chartConfig.name) {
      alert('Please enter a name for your visualization');
      return;
    }

    setSaving(true);
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch('/api/visualization/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify({
          ...chartConfig,
          id: chartConfig.id || `viz_${Date.now()}`,
          createdBy: user?.UserID,
          createdAt: new Date().toISOString()
        })
      });

      if (response.ok) {
        alert('Visualization template saved successfully!');
        await loadSavedTemplates();
        // Optionally navigate back to dashboard
        // navigate('/dashboard');
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save visualization template');
    } finally {
      setSaving(false);
    }
  };

  const exportToJSON = () => {
    const jsonString = JSON.stringify(chartConfig, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${chartConfig.name || 'visualization'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importFromJSON = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          setChartConfig(json);
          alert('Configuration imported successfully!');
        } catch (error) {
          alert('Invalid JSON file');
        }
      };
      reader.readAsText(file);
    }
  };

  const updateConfig = (path, value) => {
    setChartConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const addFilter = () => {
    const newFilter = {
      field: '',
      operator: 'equals',
      value: ''
    };
    setChartConfig(prev => ({
      ...prev,
      dataSource: {
        ...prev.dataSource,
        filters: [...prev.dataSource.filters, newFilter]
      }
    }));
  };

  const removeFilter = (index) => {
    setChartConfig(prev => ({
      ...prev,
      dataSource: {
        ...prev.dataSource,
        filters: prev.dataSource.filters.filter((_, i) => i !== index)
      }
    }));
  };

  const updateFilter = (index, field, value) => {
    setChartConfig(prev => ({
      ...prev,
      dataSource: {
        ...prev.dataSource,
        filters: prev.dataSource.filters.map((filter, i) => 
          i === index ? { ...filter, [field]: value } : filter
        )
      }
    }));
  };

  // Render Step 1: Data Source Selection
  const renderDataSourceStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Select Data Source
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Choose the entity you want to visualize data from
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dataSourceEntities.map((entity) => {
          const Icon = entity.icon;
          const isSelected = chartConfig.dataSource.entity === entity.id;
          
          return (
            <button
              key={entity.id}
              onClick={() => updateConfig('dataSource.entity', entity.id)}
              className={`
                p-6 rounded-lg border-2 transition-all text-left
                ${isSelected 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                }
              `}
            >
              <Icon className={`h-8 w-8 mb-3 ${isSelected ? 'text-blue-600' : 'text-gray-600 dark:text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {entity.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {entity.description}
              </p>
            </button>
          );
        })}
      </div>

      {chartConfig.dataSource.entity && (
        <div className="space-y-4 mt-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Configure Data Aggregation
          </h3>

          {/* Aggregation Function */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Aggregation Function
            </label>
            <select
              value={chartConfig.dataSource.aggregation}
              onChange={(e) => updateConfig('dataSource.aggregation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {aggregationFunctions.map(func => (
                <option key={func.id} value={func.id}>
                  {func.name} - {func.description}
                </option>
              ))}
            </select>
          </div>

          {/* Group By Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Group By Field
            </label>
            <select
              value={chartConfig.dataSource.groupBy}
              onChange={(e) => updateConfig('dataSource.groupBy', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Select field...</option>
              {entityFields.map(field => (
                <option key={field.name} value={field.name}>
                  {field.label} ({field.type})
                </option>
              ))}
            </select>
          </div>

          {/* Filters */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Filters (Optional)
              </label>
              <button
                onClick={addFilter}
                className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4" />
                <span>Add Filter</span>
              </button>
            </div>

            {chartConfig.dataSource.filters.map((filter, index) => (
              <div key={index} className="flex items-center space-x-2 mb-2">
                <select
                  value={filter.field}
                  onChange={(e) => updateFilter(index, 'field', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
                <select
                  value={filter.operator}
                  onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                </select>
                <input
                  type="text"
                  value={filter.value}
                  onChange={(e) => updateFilter(index, 'value', e.target.value)}
                  placeholder="Value"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={() => removeFilter(index)}
                  className="p-2 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render Step 2: Chart Type Selection
  const renderChartTypeStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Select Chart Type
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Choose how you want to visualize your data
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {chartTypes.map((type) => {
          const Icon = type.icon;
          const isSelected = chartConfig.type === type.id;
          
          return (
            <button
              key={type.id}
              onClick={() => updateConfig('type', type.id)}
              className={`
                p-6 rounded-lg border-2 transition-all text-left
                ${isSelected 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                }
              `}
            >
              <Icon className={`h-10 w-10 mb-3 ${isSelected ? 'text-blue-600' : 'text-gray-600 dark:text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {type.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {type.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Render Step 3: Dimension Configuration
  const renderDimensionStep = () => {
    const selectedChartType = chartTypes.find(t => t.id === chartConfig.type);
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Configure Dimensions
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Map your data fields to chart dimensions
          </p>
        </div>

        <div className="space-y-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          {/* Chart-specific dimension configuration */}
          {chartConfig.type === 'bar' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  X-Axis (Category) <span className="text-red-500">*</span>
                </label>
                <select
                  value={chartConfig.dimensions.xKey}
                  onChange={(e) => updateConfig('dimensions.xKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Y-Axis (Value) <span className="text-red-500">*</span>
                </label>
                <select
                  value={chartConfig.dimensions.yKey}
                  onChange={(e) => updateConfig('dimensions.yKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.filter(f => f.type === 'number').map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {chartConfig.type === 'pie' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Label Field (Name) <span className="text-red-500">*</span>
                </label>
                <select
                  value={chartConfig.dimensions.nameKey}
                  onChange={(e) => updateConfig('dimensions.nameKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Value Field <span className="text-red-500">*</span>
                </label>
                <select
                  value={chartConfig.dimensions.dataKey}
                  onChange={(e) => updateConfig('dimensions.dataKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.filter(f => f.type === 'number').map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {chartConfig.type === 'trend' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Field (X-Axis) <span className="text-red-500">*</span>
                </label>
                <select
                  value={chartConfig.dimensions.xKey}
                  onChange={(e) => updateConfig('dimensions.xKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.filter(f => f.type === 'date').map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Value Field <span className="text-red-500">*</span>
                </label>
                <select
                  value={chartConfig.dimensions.dataKey}
                  onChange={(e) => updateConfig('dimensions.dataKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select field...</option>
                  {entityFields.filter(f => f.type === 'number').map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render Step 4: Styling & Formatting
  const renderStylingStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Styling & Formatting
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Customize the appearance of your visualization
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="space-y-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Basic Information
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Visualization Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={chartConfig.name}
              onChange={(e) => updateConfig('name', e.target.value)}
              placeholder="Enter a name..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={chartConfig.description}
              onChange={(e) => updateConfig('description', e.target.value)}
              placeholder="Enter a description..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Visual Styling */}
        <div className="space-y-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Visual Styling
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={chartConfig.styling.color}
                onChange={(e) => updateConfig('styling.color', e.target.value)}
                className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded"
              />
              <input
                type="text"
                value={chartConfig.styling.color}
                onChange={(e) => updateConfig('styling.color', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {chartConfig.type === 'bar' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Layout
              </label>
              <select
                value={chartConfig.styling.layout}
                onChange={(e) => updateConfig('styling.layout', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            </div>
          )}

          {chartConfig.type === 'trend' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Chart Style
              </label>
              <select
                value={chartConfig.styling.chartType}
                onChange={(e) => updateConfig('styling.chartType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="area">Area</option>
                <option value="line">Line</option>
              </select>
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showLegend"
              checked={chartConfig.styling.showLegend}
              onChange={(e) => updateConfig('styling.showLegend', e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="showLegend" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Show Legend
            </label>
          </div>
        </div>

        {/* Formatting */}
        <div className="space-y-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg md:col-span-2">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Data Formatting
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Value Format
            </label>
            <select
              value={chartConfig.formatting.valueFormat}
              onChange={(e) => updateConfig('formatting.valueFormat', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="number">Number</option>
              <option value="currency">Currency (THB)</option>
              <option value="percentage">Percentage</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tooltip Format Template (Optional)
            </label>
            <input
              type="text"
              value={chartConfig.formatting.tooltipFormatter}
              onChange={(e) => updateConfig('formatting.tooltipFormatter', e.target.value)}
              placeholder="{name}: {value}"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Use placeholders: {'{name}'}, {'{value}'}, {'{percent}'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Label Format Template (Optional)
            </label>
            <input
              type="text"
              value={chartConfig.formatting.labelFormat}
              onChange={(e) => updateConfig('formatting.labelFormat', e.target.value)}
              placeholder="{name}: {value}"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 5: Preview & Save
  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Preview & Save
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Preview your visualization and save it
          </p>
        </div>
        <button
          onClick={fetchPreviewData}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Play className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Load Preview</span>
        </button>
      </div>

      {/* Preview Area */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {chartConfig.name || 'Untitled Visualization'}
          </h3>
          {chartConfig.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {chartConfig.description}
            </p>
          )}
        </div>

        {previewData.length > 0 ? (
          <>
            {chartConfig.type === 'pie' && (
              <PieChartComponent
                data={previewData}
                title={chartConfig.name}
                dataKey={chartConfig.dimensions.dataKey}
                loading={loading}
              />
            )}
            {chartConfig.type === 'bar' && (
              <BarChartComponent
                data={previewData}
                title={chartConfig.name}
                xKey={chartConfig.dimensions.xKey}
                yKey={chartConfig.dimensions.yKey}
                color={chartConfig.styling.color}
                layout={chartConfig.styling.layout}
                loading={loading}
              />
            )}
            {chartConfig.type === 'trend' && (
              <TrendChartComponent
                data={previewData}
                title={chartConfig.name}
                dataKey={chartConfig.dimensions.dataKey}
                color={chartConfig.styling.color}
                chartType={chartConfig.styling.chartType}
                loading={loading}
              />
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <Eye className="h-12 w-12 mb-3" />
            <p>Click "Load Preview" to see your visualization</p>
          </div>
        )}
      </div>

      {/* JSON Configuration */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Configuration JSON
          </h3>
          <button
            onClick={() => setShowJsonEditor(!showJsonEditor)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
          >
            {showJsonEditor ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span>{showJsonEditor ? 'Hide' : 'Show'}</span>
          </button>
        </div>

        {showJsonEditor && (
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
              {JSON.stringify(chartConfig, null, 2)}
            </pre>
            <button
              onClick={exportToJSON}
              className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded text-white"
              title="Export JSON"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Navigation buttons
  const renderStepNavigation = () => (
    <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
      <button
        onClick={() => {
          if (activeStep > 1) setActiveStep(activeStep - 1);
          else navigate('/dashboard');
        }}
        className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>{activeStep > 1 ? 'Previous' : 'Back to Dashboard'}</span>
      </button>

      <div className="flex items-center space-x-3">
        {activeStep < 5 ? (
          <button
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={!canProceedToNextStep()}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <>
            <button
              onClick={exportToJSON}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Download className="h-4 w-4" />
              <span>Export JSON</span>
            </button>
            <button
              onClick={saveVisualizationTemplate}
              disabled={saving || !chartConfig.name}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className={`h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
              <span>{saving ? 'Saving...' : 'Save Template'}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  const canProceedToNextStep = () => {
    switch (activeStep) {
      case 1:
        return chartConfig.dataSource.entity && chartConfig.dataSource.groupBy;
      case 2:
        return chartConfig.type;
      case 3:
        if (chartConfig.type === 'bar') {
          return chartConfig.dimensions.xKey && chartConfig.dimensions.yKey;
        } else if (chartConfig.type === 'pie') {
          return chartConfig.dimensions.nameKey && chartConfig.dimensions.dataKey;
        } else if (chartConfig.type === 'trend') {
          return chartConfig.dimensions.xKey && chartConfig.dimensions.dataKey;
        }
        return false;
      case 4:
        return chartConfig.name;
      default:
        return true;
    }
  };

  // Step indicator
  const steps = [
    { number: 1, name: 'Data Source', icon: Database },
    { number: 2, name: 'Chart Type', icon: BarChart3 },
    { number: 3, name: 'Dimensions', icon: Layers },
    { number: 4, name: 'Styling', icon: Palette },
    { number: 5, name: 'Preview', icon: Eye }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Visualization Studio
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Create powerful data visualizations for your dashboard
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-between mt-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = activeStep === step.number;
              const isCompleted = activeStep > step.number;
              
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`
                        flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all
                        ${isActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}
                        ${isCompleted ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}
                        ${!isActive && !isCompleted ? 'border-gray-300 dark:border-gray-600' : ''}
                      `}
                    >
                      <Icon
                        className={`
                          h-6 w-6
                          ${isActive ? 'text-blue-600' : ''}
                          ${isCompleted ? 'text-green-600' : ''}
                          ${!isActive && !isCompleted ? 'text-gray-400' : ''}
                        `}
                      />
                    </div>
                    <span
                      className={`
                        mt-2 text-sm font-medium
                        ${isActive ? 'text-blue-600' : ''}
                        ${isCompleted ? 'text-green-600' : ''}
                        ${!isActive && !isCompleted ? 'text-gray-500 dark:text-gray-400' : ''}
                      `}
                    >
                      {step.name}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-0.5 mx-4
                        ${isCompleted ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}
                      `}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 md:p-8">
          {activeStep === 1 && renderDataSourceStep()}
          {activeStep === 2 && renderChartTypeStep()}
          {activeStep === 3 && renderDimensionStep()}
          {activeStep === 4 && renderStylingStep()}
          {activeStep === 5 && renderPreviewStep()}

          {renderStepNavigation()}
        </div>

        {/* Saved Templates Sidebar */}
        {savedTemplates.length > 0 && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Saved Templates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setChartConfig(template)}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors text-left"
                >
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                    {template.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {template.description || 'No description'}
                  </p>
                  <div className="mt-2 flex items-center space-x-2 text-xs text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                      {template.type}
                    </span>
                    <span>{template.dataSource?.entity}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisStudio;

